import React from "react";

import {
  Box,
  Heading,
  Text,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface B2CMarkdownLongTextProps {
  content?: string;
  textAlign?: "left" | "center";
}

const B2CMarkdownLongText = ({
  content,
  textAlign = "left",
}: B2CMarkdownLongTextProps) => {

  // =========================
  // MARKDOWN THEME COLORS
  // =========================

  const markdownBg = useColorModeValue("white", "gray.800");
  const markdownTextColor = useColorModeValue("gray.700", "gray.200");
  const markdownHeadingColor = useColorModeValue("gray.800", "white");
  const markdownBorderColor = useColorModeValue("gray.200", "gray.700");
  const markdownInlineCodeBg = useColorModeValue("gray.100", "gray.700");
  const markdownCodeBlockBg = useColorModeValue("gray.900", "gray.800");
  const markdownBlockquoteBg = useColorModeValue("blue.50", "gray.700");
  const markdownBlockquoteBorder = useColorModeValue("blue.400", "blue.300");
  const markdownStrongColor = useColorModeValue("gray.900", "white");
  const markdownTableHeaderBg = useColorModeValue("gray.100", "gray.700");
  const markdownCardBg = useColorModeValue("gray.50", "gray.900");

  const markdownLinkColor = useColorModeValue("blue.600", "blue.300");
  const markdownLinkHoverColor = useColorModeValue("blue.800", "blue.200");

  if (!content) return null;

  const commonHeadingProps = {
    color: markdownHeadingColor,
    textAlign,
  };

  const isCentered = textAlign === "center";

  return (
    <Box width="100%">
      <Box 
      // bg={markdownBg} 
      width="100%">
        <Box
          className="markdown-content"
          fontSize="md"
          lineHeight="1.9"
          color={markdownTextColor}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Headings
              h1: ({ node, ...props }) => (
                <Heading size="xl" mt={0} mb={5} lineHeight="1.3" {...commonHeadingProps} {...props} />
              ),
              h2: ({ node, ...props }) => (
                <Heading size="lg" mt={8} mb={4} lineHeight="1.4" {...commonHeadingProps} {...props} />
              ),
              h3: ({ node, ...props }) => (
                <Heading size="md" mt={7} mb={3} fontWeight="semibold" lineHeight="1.5" {...commonHeadingProps} {...props} />
              ),

              // Paragraph
              p: ({ node, ...props }) => (
                <Text
                  mb={5}
                  fontSize="md"
                  lineHeight="1.95"
                  textAlign={textAlign}
                  color={markdownTextColor}
                  {...props}
                />
              ),

              // Unordered List
              ul: ({ node, ...props }) => (
                <Box
                  as="ul"
                  pl={isCentered ? 0 : 8}
                  mb={5}
                  textAlign={isCentered ? "center" : "left"}
                  listStyleType={isCentered ? "disc" : "disc"}
                  listStylePosition={isCentered ? "inside" : "outside"}
                  sx={{
                    li: {
                      marginBottom: "12px",
                      paddingLeft: isCentered ? 0 : "6px",
                    },
                  }}
                  {...props}
                />
              ),

              // Ordered List
              ol: ({ node, ...props }) => (
                <Box
                  as="ol"
                  pl={isCentered ? 0 : 8}
                  mb={5}
                  textAlign={isCentered ? "center" : "left"}
                  listStyleType={isCentered ? "decimal" : "decimal"}
                  listStylePosition={isCentered ? "inside" : "outside"}
                  sx={{
                    li: {
                      marginBottom: "12px",
                      paddingLeft: isCentered ? 0 : "6px",
                    },
                  }}
                  {...props}
                />
              ),

              // List Item
              li: ({ node, ...props }) => (
                <Box
                  as="li"
                  lineHeight="1.9"
                  color={markdownTextColor}
                  sx={{
                    "& > p": {
                      display: "inline",
                      marginBottom: 0,
                    },
                  }}
                  {...props}
                />
              ),

              // Strong
              strong: ({ node, ...props }) => (
                <Text
                  as="span"
                  fontWeight="bold"
                  color={markdownStrongColor}
                  {...props}
                />
              ),

              // Links
              a: ({ node, ...props }) => (
                <Link
                  color={markdownLinkColor}
                  textDecoration="underline"
                  textUnderlineOffset="3px"
                  fontWeight="semibold"
                  _hover={{ color: markdownLinkHoverColor }}
                  sx={{ "& span": { color: "inherit !important" } }}
                  {...props}
                />
              ),

              // Code
              code({ inline, className, children, ...props }: any) {
                if (inline) {
                  return (
                    <Box
                      as="code"
                      px={2}
                      py={1}
                      borderRadius="md"
                      bg={markdownInlineCodeBg}
                      fontSize="sm"
                      fontFamily="mono"
                      {...props}
                    >
                      {children}
                    </Box>
                  );
                }

                return (
                  <Box
                    as="pre"
                    bg={markdownCodeBlockBg}
                    color="white"
                    p={5}
                    borderRadius="xl"
                    overflowX="auto"
                    mb={6}
                    fontSize="sm"
                  >
                    <code className={className}>{children}</code>
                  </Box>
                );
              },

              // Blockquote
              blockquote: ({ node, ...props }) => (
                <Box
                  as="blockquote"
                  borderLeft="4px solid"
                  borderColor={markdownBlockquoteBorder}
                  bg={markdownBlockquoteBg}
                  px={5}
                  py={4}
                  my={6}
                  borderRadius="md"
                  fontStyle="italic"
                  textAlign={textAlign}
                  {...props}
                />
              ),

              // Table
              table: ({ node, ...props }) => (
                <Box
                  overflowX="auto"
                  mb={6}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={markdownBorderColor}
                  bg={markdownCardBg}
                >
                  <Box as="table" width="100%" sx={{ borderCollapse: "collapse" }} {...props} />
                </Box>
              ),

              thead: ({ node, ...props }) => <Box as="thead" bg={markdownTableHeaderBg} {...props} />,
              tr: ({ node, ...props }) => (
                <Box as="tr" borderBottom="1px solid" borderColor={markdownBorderColor} {...props} />
              ),
              th: ({ node, ...props }) => (
                <Box as="th" px={4} py={3} textAlign={textAlign} fontWeight="bold" fontSize="sm" {...props} />
              ),
              td: ({ node, ...props }) => (
                <Box as="td" px={4} py={3} fontSize="sm" textAlign={textAlign} {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
};

export default B2CMarkdownLongText;